import { MapPin, Monitor, Calendar } from "lucide-react"
import { useEffect } from "react"
import { useAppSelector, useAppDispatch } from "../app/hooks"
import { fetchCompanyMember, fetchCompany } from "../features/auth/authAPI"
import { updateCompanyProfilePicture, updateCompanyProfilePictureUrl, ensureCurrentUser } from "../features/auth/authSlice"
import { getProfilePictureUrl } from "../utils/imageUtils"
import { ProfileImage } from "./ProfileImage"

interface JobPreviewModalProps {
    isOpen: boolean
    onCreate: () => void
    onModify: () => void
    isCreating?: boolean
    data: {
        jobTitle: string
        companyName: string
        city: string
        workplaceType: string
        openingDate: string
        closingDate: string
        description: string
    }
}

export const JobPreviewModal = ({ isOpen, onCreate, onModify, isCreating = false, data }: JobPreviewModalProps) => {
    const dispatch = useAppDispatch()
    const accessToken = useAppSelector((state) => state.auth.accessToken)
    const user = useAppSelector((state) => state.auth.user)
    const companyProfilePictureId = useAppSelector((state) => state.auth.companyProfilePictureId)
    const companyProfilePictureUrl = useAppSelector((state) => state.auth.companyProfilePictureUrl)

    // Fetch company profile picture if not already in Redux state
    useEffect(() => {
        if (!accessToken || companyProfilePictureId !== null) return

        let isMounted = true
        const loadCompanyPicture = async () => {
            try {
                // Fetch current user to get company_member_id
                const ensuredUser = await dispatch(ensureCurrentUser()).unwrap()
                const companyMemberId = ensuredUser.company_member_id as number | undefined

                if (!companyMemberId || !isMounted) return

                // Fetch company member to get company_id
                const companyMemberResponse = await fetchCompanyMember(companyMemberId, accessToken)
                if (!isMounted) return

                const companyId = companyMemberResponse.data?.company?.id
                if (!companyId) return

                // Fetch company data to get profile_picture
                const companyResponse = await fetchCompany(companyId, accessToken)
                if (!isMounted) return

                const companyData = companyResponse.data
                const directCompanyPictureUrl =
                    typeof companyData?.profile_picture_url === "string"
                        ? companyData.profile_picture_url
                        : null

                if (companyData?.profile_picture) {
                    let pictureId: number | null = null
                    if (typeof companyData.profile_picture === "number") {
                        pictureId = companyData.profile_picture
                    } else if (
                        typeof companyData.profile_picture === "object" &&
                        "id" in companyData.profile_picture
                    ) {
                        pictureId = (companyData.profile_picture as { id: number }).id
                    }

                    if (pictureId !== null && isMounted) {
                        dispatch(updateCompanyProfilePicture(pictureId))
                        if (directCompanyPictureUrl) {
                            // Prefer direct API URL and skip file-id URL fetch.
                            dispatch(updateCompanyProfilePictureUrl(directCompanyPictureUrl))
                        } else {
                            dispatch(updateCompanyProfilePictureUrl(null))
                            // Cache URL so next time the modal opens the image shows instantly
                            const fallback = "/assets/images/Fallback-Company-No-Image.jpg"
                            getProfilePictureUrl(pictureId, accessToken || undefined, fallback)
                                .then((url) => {
                                    if (isMounted && url) {
                                        dispatch(updateCompanyProfilePictureUrl(url))
                                    }
                                })
                                .catch(() => {})
                        }
                    }
                }
            } catch (error) {
                console.error("Failed to fetch company picture:", error)
            }
        }

        loadCompanyPicture()

        return () => {
            isMounted = false
        }
    }, [accessToken, user?.company_member_id, companyProfilePictureId, dispatch])

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 bg-black/20">
            <div className="bg-white w-full max-w-2xl max-h-[calc(100vh-2rem)] rounded-md shadow-2xl overflow-y-auto animate-in fade-in zoom-in duration-200">
                {/* Banner Area */}
                <div className="bg-[#CCD4DE] h-32 relative">
                    <div className="absolute -bottom-4 left-8 rounded-md bg-white border-2 border-black p-4 shadow-lg w-24 h-24 flex items-center justify-center overflow-hidden">
                        <ProfileImage
                            profilePicture={companyProfilePictureUrl ?? companyProfilePictureId}
                            profilePictureUrl={companyProfilePictureUrl}
                            accessToken={accessToken || undefined}
                            alt="Company"
                            className="w-full h-full object-cover rounded-md border-2 border-black"
                            fallbackPath="/assets/images/Fallback-Company-No-Image.jpg"
                        />
                    </div>
                </div>

                {/* Content Area */}
                <div className="pt-6 px-8 pb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{data.jobTitle || "Job Title"}</h1>
                    <p className="text-lg font-bold text-gray-800 mb-4">{data.companyName || ""}</p>

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-500 text-sm mb-6">
                        <div className="flex items-center gap-1.5">
                            <MapPin className="w-4 h-4 text-[#A3D100]" />
                            <span>{data.city || "City"}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Monitor className="w-4 h-4 text-[#A3D100]" />
                            <span>{data.workplaceType || "Workplace Type"}</span>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-x-6 text-sm text-gray-500 mb-8">
                        <div className="flex items-center gap-2">
                            <span>Opening Date:</span>
                            <Calendar className="w-4 h-4 text-[#A3D100]" />
                            <span className="text-gray-900 font-medium">{data.openingDate || "Opening Date"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span>Deadline:</span>
                            <Calendar className="w-4 h-4 text-[#A3D100]" />
                            <span className="text-gray-900 font-medium">{data.closingDate || "Deadline"}</span>
                        </div>
                    </div>

                    <div className="space-y-6 text-gray-600 leading-relaxed text-[15px] max-h-[300px] overflow-auto pr-2 custom-scrollbar">
                        {data.description ? (
                            data.description.split("\n").map((para, i) => (para ? <p key={i}>{para}</p> : <br key={i} />))
                        ) : null}
                    </div>

                    <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <button
                            onClick={onCreate}
                            disabled={isCreating}
                            className="btn-cta w-full py-4 font-bold rounded-md text-sm uppercase disabled:opacity-60"
                        >
                            {isCreating ? "Posting..." : "Post"}
                        </button>
                        <button
                            onClick={onModify}
                            disabled={isCreating}
                            className="btn-cta w-full py-4 font-bold rounded-md text-sm uppercase disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            Modify
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}
